import { Link } from "react-router-dom";
import { MapPin, Star, Car, Bath, Waves, Accessibility, PawPrint, Navigation } from "lucide-react";
import { Button } from "@/components/ui/button";

const BeachCard = ({ beach, compact = false }) => {
  const amenities = [
    { key: "has_parking", icon: Car, label: "Parking", color: "bg-blue-100 text-blue-600" },
    { key: "has_bathrooms", icon: Bath, label: "Restrooms", color: "bg-orange-100 text-orange-600" },
    { key: "has_showers", icon: Waves, label: "Showers", color: "bg-green-100 text-green-600" },
    { key: "is_accessible", icon: Accessibility, label: "Accessible", color: "bg-purple-100 text-purple-600" },
    { key: "is_pet_friendly", icon: PawPrint, label: "Pets OK", color: "bg-amber-100 text-amber-600" },
  ];

  const activeAmenities = amenities.filter((a) => beach[a.key] === true);

  const handleDirections = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (beach.latitude && beach.longitude) {
      window.open(
        `https://www.google.com/maps/dir/?api=1&destination=${beach.latitude},${beach.longitude}`,
        "_blank"
      );
    } else if (beach.full_address) {
      window.open(
        `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(beach.full_address)}`,
        "_blank"
      );
    }
  };

  if (compact) {
    return (
      <Link
        to={`/beach/${beach.slug}`}
        className="block p-4 bg-white rounded-xl border-2 border-gray-100 hover:border-[#0EA5E9] hover:shadow-lg transition-all duration-200"
        data-testid={`beach-card-${beach.slug}`}
      >
        <h3 className="font-bold text-gray-800 font-['Outfit'] mb-1 line-clamp-1">
          {beach.name}
        </h3>
        <div className="flex items-center gap-1 text-sm text-gray-500 mb-2">
          <MapPin className="w-3 h-3 text-[#0EA5E9]" />
          <span className="line-clamp-1">{beach.city}, {beach.state}</span>
        </div>
        {beach.average_rating && (
          <div className="flex items-center gap-1">
            <Star className="w-4 h-4 fill-[#FBBF24] text-[#FBBF24]" />
            <span className="text-sm font-bold text-gray-700">
              {beach.average_rating.toFixed(1)}
            </span>
          </div>
        )}
      </Link>
    );
  }

  return (
    <div
      className="bg-white rounded-2xl border-2 border-gray-100 overflow-hidden hover:border-[#0EA5E9] hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
      data-testid={`beach-card-${beach.slug}`}
    >
      <div className="p-5">
        {/* Header */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1">
            <Link to={`/beach/${beach.slug}`}>
              <h3 className="font-bold text-lg text-gray-800 font-['Outfit'] hover:text-[#0EA5E9] transition-colors line-clamp-1">
                {beach.name}
              </h3>
            </Link>
            <div className="flex items-center gap-1 text-sm text-gray-500 mt-1">
              <MapPin className="w-3 h-3 text-[#0EA5E9] flex-shrink-0" />
              <span className="line-clamp-1">{beach.city}, {beach.state}</span>
            </div>
          </div>
          {beach.average_rating && (
            <div className="flex items-center gap-1 bg-[#FEF3C7] px-2.5 py-1 rounded-full">
              <Star className="w-4 h-4 fill-[#FBBF24] text-[#FBBF24]" />
              <span className="text-sm font-bold text-gray-700">
                {beach.average_rating.toFixed(1)}
              </span>
            </div>
          )}
        </div>

        {/* Amenities */}
        {activeAmenities.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-4">
            {activeAmenities.slice(0, 4).map((amenity) => (
              <span
                key={amenity.key}
                className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold ${amenity.color}`}
              >
                <amenity.icon className="w-3 h-3" />
                {amenity.label}
              </span>
            ))}
            {activeAmenities.length > 4 && (
              <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold bg-gray-100 text-gray-600">
                +{activeAmenities.length - 4}
              </span>
            )}
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center gap-2">
          <Link to={`/beach/${beach.slug}`} className="flex-1">
            <Button
              variant="outline"
              className="w-full border-2 border-gray-200 text-gray-700 hover:bg-gray-50 font-semibold"
              data-testid={`view-details-${beach.slug}`}
            >
              View Details
            </Button>
          </Link>
          <Button
            onClick={handleDirections}
            className="bg-[#0EA5E9] hover:bg-[#0284C7] text-white font-semibold"
            data-testid={`directions-${beach.slug}`}
          >
            <Navigation className="w-4 h-4 mr-1" />
            Go
          </Button>
        </div>
      </div>
    </div>
  );
};

export default BeachCard;
